import { ATTENTION_SIGNALS, type AttentionSignal } from "@/services/api/entityApi";
import type { UserSettings } from "../useUserSettings";
import { SettingsSection } from "./SettingsSection";

/**
 * ROW 5 — "What needs me" (D-206-010 row 5; D-206-011 decided this one GAINS A REAL CONTROL).
 *
 * **Who does what:** the person sees what is waiting for his decision right now, and says which kinds
 * of those may reach him; Raisy holds the rest and lets only the chosen kinds interrupt.
 * **What he sees:** the real counts, split by why they need him, and a switch per kind.
 * **What he feels:** *"it tells me what needs me"* (FTUE W7) — and, crucially, *he* decides what that
 * means. This is the row that answered the PO's own question (*"why am I not seeing any red icon for
 * checking a prepared draft?"*), which is why a prepared draft is a count of its own.
 *
 * ⚠️ THE CONTROL IS REAL NOW, AND THAT RETIRED A SENTENCE. Until T0302 built the store, this row
 * said *"choosing which of these may interrupt you arrives with the attention preference"* — an
 * honest statement of an absence (D-206-001 forbids a control that cannot save). The moment the store
 * existed that sentence became FALSE, so it went in the same commit as the control. **A claim about
 * what is not built yet has an expiry date, and the task that builds it owns removing it.**
 *
 * THE COUNTS REMAIN INDEPENDENT OF THE PREFERENCE, ON PURPOSE. Switching a kind off does not hide
 * what it holds — the person still sees the work waiting, he has only said it may not INTERRUPT him.
 * Folding the counts into the preference would answer "what needs me?" with "nothing", which is the
 * opposite of what this row is for.
 */
const SIGNAL_WORDS: Record<AttentionSignal, string> = {
  process_gate: "an approval of mine is waiting",
  hermes_wake: "a wake of mine is waiting",
  preparation: "a prepared draft is ready for me to review",
};

export const NeedsYouSection = ({ settings }: { settings: UserSettings }) => {
  const {
    needsYou,
    attentionPreference,
    saveAttention,
    attentionSave,
    attentionSaveError,
  } = settings;

  const value =
    needsYou.state === "loading" ? (
      <span className="text-muted-foreground">Counting what is waiting for you…</span>
    ) : needsYou.state === "unavailable" ? (
      <span className="text-muted-foreground">
        Not readable right now — {needsYou.reason}. Nothing has been lost.
      </span>
    ) : (
      (() => {
        const { process_gate, hermes_wake, preparation } = needsYou.value;
        const parts: string[] = [];
        if (process_gate > 0) parts.push(`${process_gate} ${process_gate === 1 ? "approval" : "approvals"} waiting`);
        if (hermes_wake > 0) parts.push(`${hermes_wake} ${hermes_wake === 1 ? "wake" : "wakes"} waiting`);
        if (preparation !== null && preparation > 0)
          parts.push(`${preparation} prepared ${preparation === 1 ? "draft" : "drafts"} to review`);
        const nothingWaiting = process_gate === 0 && hermes_wake === 0 && (preparation === null || preparation === 0);
        return (
          <span className="flex flex-col gap-1">
            <span>
              {parts.length > 0 ? parts.join(" · ") : "Nothing is waiting on you right now"}
              {preparation === null && parts.length > 0 && (
                <span className="ml-2 font-normal text-muted-foreground">
                  prepared drafts are not reported by this build
                </span>
              )}
            </span>
            <span className="text-xs font-normal text-muted-foreground">
              {nothingWaiting
                ? "Raisy is working; there is nothing for you to approve at this moment."
                : "These are the items Raisy is holding until you decide."}
            </span>
          </span>
        );
      })()
    );

  return (
    <SettingsSection
      title="What needs me"
      hint="What Raisy is holding for your decision right now, and which of those may interrupt you."
      value={value}
      saveState={attentionSave}
      saveError={attentionSaveError}
      control={
        attentionPreference.state === "ready" ? (
          <fieldset className="flex flex-col gap-1">
            <legend className="text-xs font-medium text-muted-foreground">Tell me about…</legend>
            {ATTENTION_SIGNALS.map((signal) => {
              const on = attentionPreference.value.signals.includes(signal);
              return (
                <label key={signal} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    aria-label={SIGNAL_WORDS[signal]}
                    checked={on}
                    disabled={attentionSave === "saving"}
                    onChange={() => {
                      const next = on
                        ? attentionPreference.value.signals.filter((s) => s !== signal)
                        : [...attentionPreference.value.signals, signal];
                      void saveAttention(next);
                    }}
                  />
                  {SIGNAL_WORDS[signal]}
                </label>
              );
            })}
            {attentionPreference.value.signals.length === 0 && (
              <span className="text-xs text-muted-foreground">
                Nothing is switched on — Raisy will hold everything quietly until you look.
              </span>
            )}
          </fieldset>
        ) : undefined
      }
    />
  );
};
